const META_PIXEL_ID = "2879865262385957";
const INITIATE_CHECKOUT_STORAGE_KEY = "meta_initiate_checkout_tracked";
const PURCHASE_STORAGE_PREFIX = "meta_purchase_tracked_";
const CONFIRMED_PURCHASE_STORAGE_KEY = "meta_confirmed_purchase";

const pageViewsTracked = new Set();
let initiateCheckoutTracked = false;
const purchasesTracked = new Set();

const canUseStorage = (storage) => {
  try {
    const testKey = "__metaPixel_test__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

const getStorageItem = (storage, key) => {
  if (!canUseStorage(storage)) return null;
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const setStorageItem = (storage, key, value) => {
  if (!canUseStorage(storage)) return;
  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore storage write failures (private mode, quota, etc.)
  }
};

const parseNumericValue = (value) => {
  const numericValue = parseFloat(value);
  return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : undefined;
};

export const getConfirmedOrderId = (responseData) => {
  const candidates = [
    responseData?.order_id,
    responseData?.orderId,
    responseData?.order_reference,
    responseData?.orderReference,
    responseData?.transaction_id,
    responseData?.transactionId,
  ];

  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && String(candidate).trim() !== "") {
      return String(candidate);
    }
  }

  return null;
};

export const getConfirmedPurchaseValue = (responseData, fallbackParts = {}) => {
  const responseCandidates = [
    responseData?.order_total,
    responseData?.total,
    responseData?.grand_total,
    responseData?.amount,
    responseData?.order_amount,
    responseData?.total_amount,
  ];

  for (const candidate of responseCandidates) {
    const parsed = parseNumericValue(candidate);
    if (parsed !== undefined) {
      return parsed;
    }
  }

  const base = parseNumericValue(fallbackParts.productPrice);
  if (base === undefined) {
    return undefined;
  }

  const discountPercent = fallbackParts.discountPercent;
  const hasPercentDiscount =
    discountPercent !== null &&
    discountPercent !== undefined &&
    discountPercent !== "" &&
    discountPercent !== "0.00" &&
    Number(discountPercent) !== 0;

  if (hasPercentDiscount) {
    return parseNumericValue(base - (base * parseFloat(discountPercent)) / 100);
  }

  const amountOff = parseNumericValue(fallbackParts.discountAmount) || 0;
  return parseNumericValue(base - amountOff);
};

export const loadMetaPixel = () => {
  if (typeof window === "undefined") return;

  const alreadyPresent = Boolean(window.fbq);

  if (!alreadyPresent) {
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */

    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
    pageViewsTracked.add(window.location.pathname);
    return;
  }

  if (window.__metaPixelBasePageViewPath) {
    pageViewsTracked.add(window.__metaPixelBasePageViewPath);
  }
};

export const trackPageView = () => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;

  const pageKey = window.location.pathname;
  if (pageViewsTracked.has(pageKey)) return;

  pageViewsTracked.add(pageKey);
  window.fbq("track", "PageView");
};

export const trackInitiateCheckout = () => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  if (initiateCheckoutTracked) return;

  if (getStorageItem(window.sessionStorage, INITIATE_CHECKOUT_STORAGE_KEY)) {
    initiateCheckoutTracked = true;
    return;
  }

  setStorageItem(window.sessionStorage, INITIATE_CHECKOUT_STORAGE_KEY, "1");
  initiateCheckoutTracked = true;
  window.fbq("track", "InitiateCheckout");
};

export const storeConfirmedPurchase = ({ orderId, value, currency = "USD" } = {}) => {
  if (!orderId) return;

  setStorageItem(
    window.sessionStorage,
    CONFIRMED_PURCHASE_STORAGE_KEY,
    JSON.stringify({
      orderId: String(orderId),
      value,
      currency,
      confirmed: true,
    })
  );
};

const purchaseStorageKey = (orderId) => `${PURCHASE_STORAGE_PREFIX}${orderId}`;

const hasTrackedPurchase = (orderId) => {
  if (!orderId) return true;
  if (purchasesTracked.has(orderId)) return true;

  const key = purchaseStorageKey(orderId);
  return Boolean(
    getStorageItem(window.localStorage, key) || getStorageItem(window.sessionStorage, key)
  );
};

const markPurchaseTracked = (orderId) => {
  purchasesTracked.add(orderId);
  const key = purchaseStorageKey(orderId);
  setStorageItem(window.localStorage, key, "1");
  setStorageItem(window.sessionStorage, key, "1");
};

export const trackPurchase = ({ orderId, value, currency = "USD" } = {}) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  if (!orderId) return;
  if (hasTrackedPurchase(orderId)) return;

  const numericValue = parseNumericValue(value);
  if (numericValue === undefined) return;

  markPurchaseTracked(orderId);

  window.fbq(
    "track",
    "Purchase",
    {
      value: numericValue,
      currency: currency || "USD",
    },
    { eventID: String(orderId) }
  );
};

export const trackPurchaseFromConfirmation = () => {
  if (typeof window === "undefined") return;

  const raw = getStorageItem(window.sessionStorage, CONFIRMED_PURCHASE_STORAGE_KEY);
  if (!raw) return;

  try {
    const confirmed = JSON.parse(raw);
    if (!confirmed?.confirmed || !confirmed?.orderId) return;
    trackPurchase(confirmed);
  } catch (error) {
    // Ignore malformed confirmation payloads
  }
};
